import clsx from 'clsx'
import { CallType } from '@weblite/ts-action/media-proxy'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LiveTribuneIcon, MeetIcon, TribuneIcon } from '@weblite/icons'
import { styled, Typography, ButtonBase, CircularProgress } from '@mui/material'
import { useOpenedRoomId } from '$logic/opened-room'
import { t } from '$i18n'
import { SCHEDULED_CALL_BAR_HEIGHT } from '$comp/_app/components/chat/components/conf'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import {
  calculateDateMetaFromSeconds,
  calculateDatesDiff,
  formatToLocaleDateTime,
} from '$helper/functions/date'
import { toLocale } from '$helper/functions/text'
import {
  callService,
  getDefaultCallTitle,
  isCallStarted,
  useJoinCallWithTimeout,
} from '$logic/call'
import { useRoom } from '$logic/room'
import {
  HallCallType,
  showChooseHallCallTimeDialog,
} from '$comp/modals/create-hall-call/choose-call-time'
import { DotPulseLoading } from '$common/loading/dot-pulse-loading/dot-pulse-loading'
import { ActiveAudioWaves } from '$common/active-audio-waves'
import { TribuneActiveAudioWaves } from '$common/tribune-active-audio-waves'
import { LiveActiveIcon } from '$common/live-active-icon/live-active-icon'
import {
  showDeleteScheduledCallConfirmationModal,
  showStartScheduledCallDialog,
} from '$comp/modals/confirmation-dialog'
import {
  dateWithOffset,
  checkTimeOffset,
  timeOffsetChangeCallbackManager,
} from '$logic/client-device/time-offset.service'
import { useCountDownToDate } from '$hooks/use-count-down-to-date'
import { useDetectSystemTimeChange } from '$logic/client-device/time-offset.react-hook'
import { useHall } from '$logic/hall/react-hook'

const ACTION_BUTTON_MAX_WIDTH = 129
const ROOT_PADDING_RIGHT = 16
const ROOT_PADDING_LEFT = 12
const CALL_TYPE_ICON_SIZE = 20
const CALL_INFO_HEADER_GAP = 6

export function NotParticipatedHallCallBar() {
  const { joinCall, joiningCall } = useJoinCallWithTimeout()
  const [willStartInLessThanOneDay, setWillStartInLessThanOneDay] =
    useState<boolean>(false)

  useDetectSystemTimeChange()

  const openedRoomId = useOpenedRoomId()
  const room = useRoom(openedRoomId || '')

  const { entity: hall } = useHall(openedRoomId)

  const roomCall = room?.call
  const roomCallStarted = useMemo(() => isCallStarted(roomCall), [roomCall])

  const { startedAt: callTime, type: callType, name: title } = roomCall || {}

  const userPermissions = useClientUserPermissions(openedRoomId || '')
  const isAdmin = userPermissions?.isAdmin || userPermissions?.isOwner

  const [callStarted, setCallStarted] = useState(roomCallStarted)
  const showActionButton = isAdmin || callStarted
  const showCountdown = willStartInLessThanOneDay && !showActionButton

  useEffect(() => {
    setCallStarted(roomCallStarted)
  }, [roomCallStarted])

  useEffect(() => {
    const updateCallTimes = () => {
      if (!callTime) return

      const { days } = calculateDatesDiff(
        new Date(dateWithOffset()),
        new Date(callTime),
      )

      setWillStartInLessThanOneDay(days === 0)
      setCallStarted(isCallStarted(roomCall))
    }

    updateCallTimes()

    timeOffsetChangeCallbackManager.addCallback(updateCallTimes)
    return () => {
      timeOffsetChangeCallbackManager.removeCallback(updateCallTimes)
    }
  }, [callTime, roomCall])

  useEffect(() => {
    checkTimeOffset()
  }, [])

  useEffect(() => {
    if (roomCallStarted || !callTime) return

    const { days } = calculateDatesDiff(
      new Date(dateWithOffset()),
      new Date(callTime),
    )

    setWillStartInLessThanOneDay(days === 0)
  }, [callTime, roomCallStarted])

  const handleStartCallFromCountdown = useCallback(() => {
    setCallStarted(true)
  }, [])

  const handleJoinCall = useCallback(
    async (getConfirmation = true) => {
      if (!room || !openedRoomId || joiningCall || !callType) return

      try {
        if (getConfirmation) await showStartScheduledCallDialog(callType)

        joinCall(openedRoomId, room.type, callType)
      } catch {
        //
      }
    },
    [openedRoomId, room, joiningCall, callType, joinCall],
  )

  const handleClickBackgroundButton = useCallback(async () => {
    if (!room || joiningCall || !callType) return
    if (callStarted) {
      handleJoinCall(false)
      return
    }
    if (!isAdmin || !callTime) return

    const defaultTitle = getDefaultCallTitle(
      hall?.name || '',
      callType as HallCallType,
    )
    const newCallTime = await showChooseHallCallTimeDialog({
      callType: callType as HallCallType,
      // hallType: room?.type as HallType,
      defaultTitle: title,
      defaultTime: new Date(callTime),
      editMode: true,
      titlePlaceholder: defaultTitle,
    })
    if (newCallTime === 'DELETE') {
      try {
        await showDeleteScheduledCallConfirmationModal(callType)
        callService.unscheduleCall(room.id, room.type, callType)
        return
      } catch {
        return
      }
    }
    const {
      time,
      title: newTitle,
      shouldStartNow,
      shouldSendNotificationMessage,
    } = newCallTime
    if (shouldStartNow) {
      handleJoinCall(false)
      return
    }
    callService.updateScheduledCall(
      room.id,
      room.type,
      callType,
      shouldSendNotificationMessage,
      title,
      callTime,
      newTitle || defaultTitle,
      +time,
    )
  }, [
    handleJoinCall,
    room,
    joiningCall,
    callStarted,
    callType,
    callTime,
    isAdmin,
    title,
    hall,
  ])

  if (
    callType !== 'tribune' &&
    callType !== 'live' &&
    callType !== 'group_call' &&
    callType !== 'classroom'
  )
    return null

  return (
    <Root>
      <BackgroundButton
        onClick={handleClickBackgroundButton}
        className={clsx({
          tribune: callType === 'tribune',
          live: callType === 'live',
          meeting: callType === 'group_call',
          classroom: callType === 'classroom',
        })}
      >
        <CallInfoHeader
          sx={{
            maxWidth: `calc(100% - ${
              showActionButton || showCountdown
                ? ACTION_BUTTON_MAX_WIDTH + 2
                : 0
            }px)`,
          }}
        >
          <IconSelector callStarted={callStarted} callType={callType} />
          <CallInfoTitle
            sx={{
              width: `calc(100% - ${
                CALL_TYPE_ICON_SIZE + CALL_INFO_HEADER_GAP
              }px)`,
            }}
          >
            <Typography
              variant="subtitle3"
              color="neutral900"
              noWrap
              maxWidth="100%"
            >
              {title || t(`call.${callType}`)}
              {!title && callType === 'tribune' && (
                <Typography
                  variant="caption1"
                  color="neutral700"
                  component="span"
                >
                  {` (${t('call.voiceCall')})`}
                </Typography>
              )}
            </Typography>
            {callStarted && (
              <Typography
                sx={{ display: 'flex', alignItems: 'center' }}
                variant="caption1"
                color="success700"
              >
                {t('call.inProgress')}{' '}
                <DotPulseLoading
                  size={5}
                  sx={{ color: 'success700', mr: '4px' }}
                />
              </Typography>
            )}
          </CallInfoTitle>
        </CallInfoHeader>
        {!callStarted && (
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            variant="caption1"
            color="neutral700"
          >
            {willStartInLessThanOneDay && isAdmin ? (
              <>
                <Typography variant="inherit" color="inherit">
                  {t('call.startsFrom')}
                </Typography>

                {callTime && (
                  <StartsFrom
                    date={callTime}
                    onCallStarted={handleStartCallFromCountdown}
                  />
                )}
              </>
            ) : (
              `${t('call.startsAtDate')} ${formatToLocaleDateTime(
                new Date(callTime || Date.now()),
              )}`
            )}
          </Typography>
        )}
      </BackgroundButton>
      {showActionButton && (
        <ActionButton
          className={clsx({
            tribune: callType === 'tribune',
            live: callType === 'live',
            meeting: callType === 'group_call',
            disabled: joiningCall,
            classroom: callType === 'classroom',
          })}
          disabled={joiningCall}
          onClick={() => {
            handleJoinCall(!callStarted)
          }}
        >
          {/* {!isAdmin &&
            (isNotificationActive ? (
              <AlarmAddIcon
                sx={{ color: 'inherit', width: '20px', height: '20px' }}
              />
            ) : (
              <AlarmOnIcon
                sx={{ color: 'inherit', width: '20px', height: '20px' }}
              />
            ))} */}
          {joiningCall ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            <Typography variant="subtitle4" color="inherit" zIndex={1}>
              {callStarted
                ? callType === 'group_call'
                  ? t('call.join')
                  : callType === 'classroom'
                    ? t('call.enter')
                    : t('call.watch')
                : t('call.startNow')}
            </Typography>
          )}
        </ActionButton>
      )}
      {showCountdown && callTime && (
        <CountdownWrapper>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            variant="caption1"
            color="neutral700"
          >
            <Typography variant="inherit" color="inherit">
              {t('call.startsFrom')}
            </Typography>
            <StartsFrom
              date={callTime}
              onCallStarted={() => setCallStarted(true)}
            />
          </Typography>
        </CountdownWrapper>
      )}
    </Root>
  )
}

function StartsFrom({
  date,
  onCallStarted,
}: {
  date: number
  onCallStarted: () => void
}) {
  const timeLeft = useCountDownToDate(date)

  useEffect(() => {
    if (timeLeft === 0) {
      onCallStarted()
    }
  }, [timeLeft, onCallStarted])

  const {
    seconds: s,
    minutes: m,
    hours: h,
  } = useMemo(() => {
    return calculateDateMetaFromSeconds(timeLeft)
  }, [timeLeft])

  return (
    <Typography
      variant="caption1"
      color="success700"
      dir="ltr"
      mr="3px"
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <Typography
        width="16px"
        variant="inherit"
        color="inherit"
        textAlign="center"
      >
        {`${toLocale(h, false, 2)} `}
      </Typography>
      <Typography variant="inherit" color="inherit">
        :
      </Typography>
      <Typography
        width="16px"
        variant="inherit"
        color="inherit"
        textAlign="center"
      >
        {` ${toLocale(m, false, 2)} `}
      </Typography>
      <Typography variant="inherit" color="inherit">
        :
      </Typography>
      <Typography
        width="16px"
        variant="inherit"
        color="inherit"
        textAlign="center"
      >
        {` ${toLocale(s, false, 2)}`}
      </Typography>
    </Typography>
  )
}

function IconSelector({
  callType,
  callStarted,
}: {
  callType: CallType
  callStarted: boolean
}) {
  const sharedSx = { width: CALL_TYPE_ICON_SIZE, height: CALL_TYPE_ICON_SIZE }

  if (!callStarted) {
    if (callType === 'tribune')
      return <TribuneIcon sx={{ ...sharedSx, color: 'tribune500' }} />
    if (callType === 'live' || callType === 'classroom')
      return <LiveTribuneIcon sx={{ ...sharedSx, color: 'live500' }} />
    if (callType === 'group_call')
      return <MeetIcon sx={{ ...sharedSx, color: 'primary500' }} />
  } else {
    if (callType === 'group_call') return <ActiveAudioWaves />
    if (callType === 'live' || callType === 'classroom')
      return <LiveActiveIcon />
    if (callType === 'tribune') return <TribuneActiveAudioWaves />
  }

  return null
}

const Root = styled('div')`
  width: 100%;
  height: ${SCHEDULED_CALL_BAR_HEIGHT}px;
  position: relative;
`

const BackgroundButton = styled(ButtonBase)`
  align-items: flex-start;
  position: relative;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;

  width: 100%;
  height: 100%;
  padding: 0 ${ROOT_PADDING_RIGHT}px 0 ${ROOT_PADDING_LEFT}px;
  background: linear-gradient(270deg, #ffe0ed 0%, #ffa3c9 100%);
  &.tribune {
    background: ${({ theme }) => theme.palette.tribuneGradientL};
  }
  &.live {
    background: ${({ theme }) => theme.palette.liveGradientL};
  }
  &.classroom {
    background: ${({ theme }) => theme.palette.liveGradientL};
  }
  &.meeting {
    background: ${({ theme }) => theme.palette.meetingGradientL};
  }
`

const CallInfoHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: ${CALL_INFO_HEADER_GAP}px;
`

const CallInfoTitle = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const CountdownWrapper = styled('div')`
  background: linear-gradient(
    1.45deg,
    rgba(255, 255, 255, 0.4) 1.25%,
    rgba(255, 255, 255, 0.5) 98.79%
  );
  border-radius: 12px;
  position: absolute;
  top: 12px;
  left: 12px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows[1]};
  width: 129px;
`

const ActionButton = styled(ButtonBase)`
  max-width: ${ACTION_BUTTON_MAX_WIDTH}px;
  height: 32px;
  min-width: 80px;
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 7px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 12px;
  color: ${({ theme }) => theme.palette.neutral000};
  background: linear-gradient(180deg, #7c63f8 0%, #5454f7 100%);
  z-index: 1;
  &.tribune {
    background: ${({ theme }) => theme.palette.tribuneGradientH};
  }
  &.meeting {
    background: ${({ theme }) => theme.palette.meetingGradientH};
  }
  &.live {
    background: ${({ theme }) => theme.palette.liveGradientH};
  }
  &.classroom {
    background: ${({ theme }) => theme.palette.liveGradientH};
  }
  &[data-is-admin='false'] {
    background: ${({ theme }) => theme.palette.neutral600};
    &[data-is-notification-active='false'] {
      &[data-call-type='tribune'] {
        background: ${({ theme }) => theme.palette.tribuneGradientH};
      }
      &[data-call-type='live'] {
        background: ${({ theme }) => theme.palette.liveGradientH};
      }
      &[data-call-type='classroom'] {
        background: ${({ theme }) => theme.palette.liveGradientH};
      }
      &[data-call-type='group_call'] {
        background: ${({ theme }) => theme.palette.meetingGradientH};
      }
    }
  }
  &.disabled {
    opacity: 0.8;
  }
`
